import { useState, useEffect, useCallback, useRef } from 'react';
import { devLog } from "../utils/devLog";

/**
 * Custom hook for Checkout.com Card Component (tokenization only)
 * 
 * Similar to useDlocalSmartFields but for Checkout.com
 * Uses Card Component + tokenize() method instead of Flow + payment session
 * 
 * @param {Object} config
 * @param {string} config.publicKey - Checkout.com public key
 * @param {string} config.environment - 'sandbox' or 'production'
 * @param {Object} config.appearance - Styling configuration
 * @param {Function} config.onReady - Ready callback
 * @param {Function} config.onError - Error callback
 * @param {Function} config.onBinChanged - Card BIN change callback
 * 
 * @returns {Object} Hook state and methods
 * 
 * Docs: https://www.checkout.com/docs/payments/accept-payments/accept-a-payment-on-your-website/flow-library-reference/flowcomponent#tokenize():_Promise(TokenizeResult)
 */
export const useCheckoutCard = ({
  publicKey,
  paymentSession,
  environment = 'sandbox',
  appearance = {},
  componentOptions = {},
  onReady,
  onError,
  onBinChanged,
}) => {
  const [checkoutInstance, setCheckoutInstance] = useState(null);
  const [cardComponent, setCardComponent] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cardMetadata, setCardMetadata] = useState(null);
  
  const scriptRef = useRef(null);
  const cardRef = useRef(null);
  const checkoutRef = useRef(null);
  const mountedRef = useRef(false);
  
  // Store callbacks in refs to avoid re-initialization
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  const onBinChangedRef = useRef(onBinChanged);
  
  useEffect(() => {
    onReadyRef.current = onReady;
    onErrorRef.current = onError;
    onBinChangedRef.current = onBinChanged;
  }, [onReady, onError, onBinChanged]);

  /**
   * Load Checkout.com SDK dynamically
   */
  useEffect(() => {
    if (!publicKey || !paymentSession) {
      console.warn('useCheckoutCard: publicKey and paymentSession are required');
      return;
    }

    // Check if SDK already loaded
    if (window.CheckoutWebComponents) {
      initializeCard();
      return;
    }

    // Prevent duplicate loading
    if (scriptRef.current) {
      return;
    }

    const loadScript = () => {
      return new Promise((resolve, reject) => {
        devLog(`Loading Checkout.com SDK (${environment})`);
        
        const script = document.createElement('script');
        script.src = 'https://checkout-web-components.checkout.com/index.js';
        script.async = false;
        script.id = 'checkout-web-components-sdk';
        
        script.onload = () => {
          devLog('SDK loaded, waiting for initialization...');
          
          let attempts = 0;
          const maxAttempts = 30;
          
          const checkSDK = () => {
            attempts++;
            
            if (window.CheckoutWebComponents && typeof window.CheckoutWebComponents === 'function') {
              resolve(window.CheckoutWebComponents);
            } else if (attempts >= maxAttempts) {
              reject(new Error(`SDK failed to initialize. Type: ${typeof window.CheckoutWebComponents}`));
            } else {
              setTimeout(checkSDK, 50);
            }
          };
          
          setTimeout(checkSDK, 50);
        };
        
        script.onerror = () => {
          reject(new Error('Failed to load Checkout.com SDK'));
        };

        document.head.appendChild(script);
        scriptRef.current = script;
      });
    };

    setLoading(true);
    loadScript()
      .then(() => {
        initializeCard();
      })
      .catch((err) => {
        console.error('❌ SDK loading error:', err);
        setError(err.message);
        onErrorRef.current?.(err.message);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      mountedRef.current = false;
    };
  }, [publicKey, paymentSession, environment]);

  /**
   * Initialize card component
   */
  const initializeCard = async () => {
    if (!window.CheckoutWebComponents) {
      console.warn('CheckoutWebComponents not available yet');
      return;
    }

    // If card already exists, unmount and clean up before creating new one
    if (cardRef.current) {
      devLog('Card already exists, cleaning up before re-initialization');
      try {
        if (mountedRef.current) {
          cardRef.current.unmount();
        }
        cardRef.current = null;
        setCardComponent(null);
        setReady(false);
        mountedRef.current = false;
      } catch (err) {
        console.warn('Error during cleanup:', err);
      }
    }

    try {
      
      // Initialize CheckoutWebComponents WITH payment session
      const checkout = await window.CheckoutWebComponents({
        paymentSession,
        publicKey,
        environment,
        appearance,
        componentOptions,
      });
      
      checkoutRef.current = checkout;
      setCheckoutInstance(checkout);
      
      // Create card component with event handlers
      const card = checkout.create('card', {
        // Event: Card ready
        onReady: (component) => {
          setReady(true);
          onReadyRef.current?.(component);
        },
        
        // Event: Card BIN changed (first 8 digits)
        onCardBinChanged: (component, metadata) => {
          setCardMetadata(metadata);
          onBinChangedRef.current?.(metadata);
          
          // Return { continue: true } to accept card
          return { continue: true };
        },
        
        // Event: Error occurred
        onError: (component, err) => {
          const errorMessage = err.message || 'Card validation error';
          console.error('❌ Card error:', errorMessage);
          setError(errorMessage);
          onErrorRef.current?.(errorMessage);
        },
        
        // Event: Card data changed
        onChange: (component) => {
          // Clear error when user types
          if (error) {
            setError(null);
          }
        },
      });
            
      // Reset mounted flag for new card instance
      mountedRef.current = false;
      
      cardRef.current = card;
      setCardComponent(card);

    } catch (err) {
      console.error('❌ Error initializing card:', err);
      const errorMsg = err.message || 'Failed to initialize card';
      setError(errorMsg);
      onErrorRef.current?.(errorMsg);
    }
  };

  /**
   * Mount card to DOM element
   */
  const mount = useCallback(async (target) => {
    if (!cardRef.current) {
      console.warn('Card not initialized yet');
      return;
    }

    // Guard: Prevent mounting if already mounted
    if (mountedRef.current) {
      console.warn('Card already mounted, skipping mount attempt');
      return;
    }

    try {      
      // Check if card component is available
      const isAvailable = await cardRef.current.isAvailable();
      if (!isAvailable) {
        throw new Error('Card component is not available');
      }
      
      
      let targetElement;
      if (typeof target === 'string') {
        targetElement = document.querySelector(target);
        if (!targetElement) {
          throw new Error(`DOM element not found: ${target}`);
        }
      } else if (target instanceof HTMLElement) {
        targetElement = target;
      } else {
        throw new Error('Invalid target: must be selector or HTMLElement');
      }
      
      // Mount card
      cardRef.current.mount(targetElement);
      mountedRef.current = true;
      
    } catch (err) {
      console.error('❌ Error mounting card:', err);
      const errorMsg = err.message || 'Failed to mount card';
      setError(errorMsg);
      onErrorRef.current?.(errorMsg);
    }
  }, []);

  /**
   * Unmount card from DOM
   */
  const unmount = useCallback(() => {
    if (!cardRef.current) return;

    try {
      cardRef.current.unmount();
      cardRef.current = null;
      setCardComponent(null);
      setReady(false);
      mountedRef.current = false;
    } catch (err) {
      console.error('Error unmounting card:', err);
    }
  }, []);

  /**
   * Tokenize card data (like dLocal's createToken)
   * 
   * @returns {Promise<Object>} TokenizeResult with { type, data, cardMetadata }
   * 
   * Example response:
   * {
   *   type: 'card',
   *   data: {
   *     token: 'tok_xxx',
   *     expires_on: '2025-12-31T23:59:59Z',
   *     card_type: 'Credit',
   *     expiry_month: 12,
   *     expiry_year: 2025,
   *     scheme: 'VISA',
   *     last4: '4242',
   *     bin: '424242'
   *   },
   *   cardMetadata: { ... }
   * }
   */
  const tokenize = useCallback(async () => {
    if (!cardRef.current) {
      throw new Error('Card component not initialized');
    }

    if (!ready) {
      throw new Error('Card not ready');
    }

    try {      
      // Call tokenize() - similar to dLocal's createToken()
      const result = await cardRef.current.tokenize();
      
      if (!result.data?.token) {
        throw new Error('Tokenization did not return a token');
      }

      return result; // Return full result with token and metadata
      
    } catch (err) {
      console.error('❌ Tokenization error:', err);
      const errorMsg = err.message || 'Failed to tokenize card';
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  }, [ready]);

  /**
   * Check if card is valid
   */
  const isValid = useCallback(() => {
    if (!cardRef.current) return false;
    return cardRef.current.isValid();
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // SDK instance
    checkoutInstance,
    cardComponent,
    
    // State
    ready,
    error,
    loading,
    cardMetadata, // Includes card brand, type, etc.
    
    // Methods
    mount,
    unmount,
    tokenize,   // ← Like dLocal's createToken()
    isValid,
    clearError,
  };
};

export default useCheckoutCard;

